'use client';

import React from 'react';
import Link from 'next/link';
import { MapPin, ArrowUp, ArrowDown, ArrowRight } from 'lucide-react';
import type { LocationData, PageType } from '@/types/locations';
import { isIndexedLocationPage } from '@/lib/locationTiers';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(amount);
}

type SortKey = 'name' | 'region' | 'avgHousePrice' | 'annualLivingCosts' | 'retirementNumber' | 'totalNetWorth';
type SortDir = 'asc' | 'desc';

function SortIcon({ column, sortKey, sortDir }: { column: SortKey; sortKey: SortKey; sortDir: SortDir }) {
  if (sortKey !== column) return null;
  return sortDir === 'asc' ? <ArrowUp className="w-4 h-4 inline ml-1" /> : <ArrowDown className="w-4 h-4 inline ml-1" />;
}

function TableHeader({ column, label, icon, align, sortKey, sortDir, onClick }: {
  column: SortKey;
  label: string;
  icon?: React.ReactNode;
  align?: 'left' | 'right';
  sortKey: SortKey;
  sortDir: SortDir;
  onClick: (key: SortKey) => void;
}) {
  return (
    <th
      className={`text-${align} p-4 font-bold cursor-pointer hover:bg-gray-100 select-none`}
      onClick={() => onClick(column)}
    >
      <span className={`inline-flex items-center ${align === 'right' ? 'justify-end' : 'justify-start'} gap-1`}>
        {icon && <>{icon}</>}
        {label}
        <SortIcon column={column} sortKey={sortKey} sortDir={sortDir} />
      </span>
    </th>
  );
}

interface AllLocationsTableProps {
  locations: LocationData[];
  pageType: PageType;
  currentLocationSlug: string;
  currentLocationRegion?: string;
}

const PAGE_TYPE_LABELS: Record<PageType, string> = {
  'how-much-do-i-need-to-retire': 'How much do you need to retire in',
  'financial-advisors': 'Financial Advisers in',
  'wealth-managers': 'Wealth Managers in',
  'financial-planners': 'Financial Planners in',
  'financial-coaching': 'Financial Coaching in',
  'retirement-planning': 'Retirement planning in',
  'financial-guidance': 'Financial Guidance in',
  'financial-mentoring': 'Financial Mentoring in',
  'financial-therapists': 'Financial Therapists in',
};

export default function AllLocationsTable({ locations, pageType, currentLocationSlug, currentLocationRegion }: AllLocationsTableProps) {
  const [sortKey, setSortKey] = React.useState<SortKey>('name');
  const [sortDir, setSortDir] = React.useState<SortDir>('asc');

  const filteredLocations = React.useMemo(() => {
    if (!currentLocationRegion) return locations.filter(loc => loc.slug !== currentLocationSlug);
    return locations.filter(loc =>
      loc.slug !== currentLocationSlug && loc.region === currentLocationRegion
    );
  }, [locations, currentLocationSlug, currentLocationRegion]);

  const sortedLocations = React.useMemo(() => {
    return [...filteredLocations].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;
      if (sortKey === 'totalNetWorth') {
        aVal = a.avgHousePrice + a.retirementNumber;
        bVal = b.avgHousePrice + b.retirementNumber;
      } else {
        aVal = a[sortKey as keyof LocationData] as string | number;
        bVal = b[sortKey as keyof LocationData] as string | number;
      }
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredLocations, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const regionDisplay = currentLocationRegion
    ? currentLocationRegion.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    : 'UK';

  return (
    <section className="mt-12">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Explore Other Locations in {regionDisplay}</h2>
      <div className="overflow-x-auto border-2 border-ink">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b-2 border-ink">
              <TableHeader column="name" label="Location" icon={<MapPin className="w-4 h-4" />} align="left" sortKey={sortKey} sortDir={sortDir} onClick={handleSort} />
              <TableHeader column="region" label="Region" align="left" sortKey={sortKey} sortDir={sortDir} onClick={handleSort} />
              <TableHeader column="avgHousePrice" label="Avg House Price" sortKey={sortKey} sortDir={sortDir} onClick={handleSort} />
              <TableHeader column="annualLivingCosts" label="Living Costs" sortKey={sortKey} sortDir={sortDir} onClick={handleSort} />
              <TableHeader column="retirementNumber" label="Avg retirement number @ 4% SWR" sortKey={sortKey} sortDir={sortDir} onClick={handleSort} />
              <TableHeader column="totalNetWorth" label="Avg total net worth required" sortKey={sortKey} sortDir={sortDir} onClick={handleSort} />
            </tr>
          </thead>
          <tbody>
            {sortedLocations.map(loc => {
              const linkType = isIndexedLocationPage(loc, pageType) ? pageType : 'how-much-do-i-need-to-retire';
              return (
              <tr key={loc.slug} className="border-b border-ink hover:bg-gray-50">
                <td className="p-4 text-left">
                  <Link
                    href={`/locations/${loc.slug}/${linkType}`}
                    className={`hover:underline font-medium ${loc.slug === currentLocationSlug ? 'text-positive-600' : 'text-positive-600'}`}
                  >
                    {PAGE_TYPE_LABELS[linkType]} {loc.name}
                  </Link>
                </td>
                <td className="p-4 text-left text-gray-600">{loc.region ? loc.region.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '-'}</td>
                <td className="text-right p-4 text-gray-600">{formatCurrency(loc.avgHousePrice)}</td>
                <td className="text-right p-4 text-gray-600">{formatCurrency(loc.annualLivingCosts)}</td>
                <td className="text-right p-4 font-bold text-positive-700">{formatCurrency(loc.retirementNumber)}</td>
                <td className="text-right p-4 font-bold text-positive-700">{formatCurrency(loc.avgHousePrice + loc.retirementNumber)}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-6 flex gap-4">
        <Link
          href="/locations"
          className="inline-flex items-center px-6 py-3 bg-white text-gray-900 font-bold rounded-none hover:bg-gray-100 transition-colors border-2 border-gray-900 shadow-[2px_2px_0px_0px_rgba(26,26,26,1)]"
        >
          All locations
        </Link>
        <Link
          href="/where-can-i-retire-today"
          className="inline-flex items-center px-6 py-3 bg-positive-600 text-white font-bold rounded-none hover:bg-positive-700 transition-colors border-2 border-gray-900 shadow-[2px_2px_0px_0px_rgba(26,26,26,1)]"
        >
          Where can I retire today?
          <ArrowRight className="w-4 h-4 ml-2" />
        </Link>
      </div>
    </section>
  );
}
